import { useToken } from '@chakra-ui/react';

import { DivergingBarWithArrows, FormatType } from '@revelio/replots';

import { usePlotMetric } from '../../hooks/usePlotMetric';
import type {
  BreakdownSingleDataSet,
  SingleDataSet,
} from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { useActiveColors } from './GroupedBar/useActiveColors';
import { getPrettyFormatValue } from './utils/format';
import { getFormat } from './utils/getFormat';
import { getDivergingBarConfig } from './utils/map-to-diverging-data';

export const DivergingBar = ({
  dataSet,
}: {
  dataSet: BreakdownSingleDataSet | SingleDataSet;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();

  const { colorMap } = useActiveColors();

  const format = (() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    if (metric === 'sentiment_top_bottom') {
      return FormatType.PERCENTAGE;
    }
    if (item?.plot?.comparisonTransformation) {
      return FormatType.DECIMAL;
    }
    return getFormat({ metric });
  })();

  const formatValue = getPrettyFormatValue(format);
  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const arrowLabelColor = useToken('colors', 'gray.700');
  const arrowLabelFont = useToken('fonts', 'body');

  const {
    divergingBarData,
    leftColor,
    rightColor,
    leftFooterLabel,
    rightFooterLabel,
    tooltipContent,
    valueLabel,
  } = getDivergingBarConfig(dataSet, item, colorMap, formatValue);

  const divergingBar = item?.plot?.divergingBar;
  const arrowOrientation = divergingBar?.arrowOrientation ?? 'horizontal';
  const horizontalArrows =
    arrowOrientation === 'horizontal' && (leftFooterLabel || rightFooterLabel)
      ? { leftLabel: leftFooterLabel, rightLabel: rightFooterLabel }
      : undefined;
  const verticalArrows =
    arrowOrientation === 'vertical' &&
    (divergingBar?.positiveLabel || divergingBar?.negativeLabel)
      ? {
          topLabel: divergingBar?.positiveLabel,
          bottomLabel: divergingBar?.negativeLabel,
          color: arrowLabelColor,
          fontFamily: arrowLabelFont,
        }
      : undefined;

  return (
    <DivergingBarWithArrows
      data={divergingBarData}
      format={format}
      leftColor={leftColor}
      rightColor={rightColor}
      tooltipContent={tooltipContent}
      showBarLabels={showBarLabels}
      valueLabel={valueLabel}
      horizontalArrows={horizontalArrows}
      verticalArrows={verticalArrows}
    />
  );
};
